import { Component, HostListener, OnDestroy, OnInit, inject } from '@angular/core'; // Added OnDestroy
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { NGXLogger } from 'ngx-logger';
import { MessageService, PrimeTemplate } from 'primeng/api';
import { ButtonDirective } from 'primeng/button';
import { ProgressBarModule } from 'primeng/progressbar';
import { ToastModule } from 'primeng/toast';
import { Subject, filter, map, takeUntil } from 'rxjs'; // Added Subject, takeUntil

import { AuthGuardService } from './auth/services/auth-guard.service';
import { UserService } from './core/data-services/user.service';
import { UtilsDataService } from './core/data-services/utils-data.service';
import { PosthogService } from './core/services/posthog.service'; // Added PosthogService import
import { UserSelectors } from './core/store/user';
import { MobileService } from './core/system/mobile.service';
import { ToastAlertService } from './core/system/toast-alerts.service';
import { ToastService } from './core/system/toast.service';
import { LoadingBarComponent } from '../../projects/dataclouder/ngx-core/src/lib/components/loading-bar/loading-bar';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  providers: [MessageService],
  standalone: true,
  imports: [RouterOutlet, ToastModule, PrimeTemplate, ButtonDirective, ProgressBarModule, LoadingBarComponent],
})
export class AppComponent implements OnInit, OnDestroy {
  // Implement OnDestroy
  private messageService = inject(MessageService);
  private toastService = inject(ToastService);
  private toastAlertService = inject(ToastAlertService);
  private store = inject(Store);
  guard = inject(AuthGuardService);
  private swUpdate = inject(SwUpdate);
  private mobileService = inject(MobileService);
  private logger = inject(NGXLogger);
  private router = inject(Router);
  private toastrService = inject(ToastService);
  private utilsDataService = inject(UtilsDataService);
  private translate = inject(TranslateService);
  private userService = inject(UserService);
  private posthogService = inject(PosthogService); // Injected PosthogService

  public isLoading = this.store.select(UserSelectors.getIsLoading);
  private destroy$ = new Subject<void>(); // Subject for unsubscribing

  @HostListener('window:beforeinstallprompt', ['$event'])
  private onBeforeInstallPrompt(event: Event) {
    this.logger.log('event install pwa app ');
    event.preventDefault();
    this.mobileService.eventInstall = event;
  }

  // Removed problematic constructor overloads
  constructor() {
    // Using inject() means we don't need complex constructor logic for DI
    this.toastService.messageService = this.messageService;
    this.toastAlertService.setMessageService(this.messageService);
  }

  public async ngOnInit() {
    this.updatePWA();
    this.utilsDataService.getServerStatus();

    // 1) checa configuración de usuario, 2) checa configuración de navegador, default es español
    const user = this.userService.getUserSnapshot();
    if (user?.settings?.baseLanguage) {
      this.translate.use(user.settings.baseLanguage);
    } else {
      if (navigator?.language?.includes('en')) {
        this.translate.use('en');
      } else {
        this.translate.use('es');
      }
    }

    this.trackPageViews(); // Call the page view tracking method
  }

  ngOnDestroy(): void {
    // Implement OnDestroy
    this.destroy$.next();
    this.destroy$.complete();
  }

  private trackPageViews(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntil(this.destroy$), // Use the local Subject for unsubscription
      )
      .subscribe((event: NavigationEnd) => {
        this.posthogService.capturePageView();
        this.logger.debug(`PostHog pageview captured for URL: ${event.urlAfterRedirects}`);
      });
  }

  private updatePWA(): void {
    this.swUpdate.versionUpdates
      .pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'), // Removed 'any' type
        map((evt: VersionReadyEvent) => ({
          // Use specific type
          type: 'UPDATE_AVAILABLE',
          current: evt.currentVersion,
          available: evt.latestVersion,
        })),
        // Removed takeUntil(this.destroy$) here - SwUpdate subscription should likely persist
      )
      .subscribe(() => {
        this.toastrService.info('Descargado...', 'Se tiene una pequeña actualización');

        setTimeout(() => {
          window.location.reload();
        }, 2000);
      });
  }

  public toSocialNetwork(social: string) {
    let url;
    if (social === 'instagram') {
      url = 'https://www.instagram.com/polilan_app';
    } else if (social === 'tiktok') {
      url = 'https://www.tiktok.com/@polilan_app';
    } else if (social === 'youtube') {
      url = 'https://www.youtube.com/@polilan_app';
    }

    window.open(url, '_blank');
  }
}
